import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, Stethoscope } from "lucide-react";
import { StarRating } from "@/components/StarRating";

export default function ServiceDirectory() {
  const [providers, setProviders] = useState([]);
  const [type, setType] = useState("all");

  useEffect(() => {
    const p = {};
    if (type !== "all") p.service_type = type;
    api.get("/providers", { params: p }).then(r => setProviders(r.data.providers));
  }, [type]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <h1 className="heading text-3xl sm:text-4xl font-extrabold tracking-tight">Transport & Vet Directory</h1>
      <p className="text-stone-600 mt-2">Accredited cold-chain couriers and veterinary services for bio-material shipments.</p>

      <div className="mt-6 max-w-xs">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="provider-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All services</SelectItem>
            <SelectItem value="transport">Transport / Cold-chain</SelectItem>
            <SelectItem value="vet">Veterinary</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-5">
        {providers.map((p) => (
          <div key={p.user_id} data-testid={`provider-card-${p.user_id}`} className="surface-card p-5">
            <div className="flex items-center gap-3">
              <div className={`h-11 w-11 rounded-lg grid place-items-center ${p.service_type === "transport" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>
                {p.service_type === "transport" ? <Truck className="h-5 w-5" /> : <Stethoscope className="h-5 w-5" />}
              </div>
              <div>
                <div className="heading text-lg font-semibold">{p.business_name}</div>
                <div className="text-xs uppercase mono text-stone-500">{p.service_type}</div>
              </div>
              <div className="ml-auto flex items-center gap-1.5 text-sm">
                <StarRating value={p.rating_avg || 0} size="h-3.5 w-3.5" testId={`provider-rating-${p.user_id}`} />
                <span className="mono">{p.rating_avg?.toFixed(1) || "—"}</span>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-[10px] uppercase tracking-widest text-stone-400">Service areas</div><div>{(p.service_areas || []).join(", ")}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-stone-400">Contact</div><div className="mono text-xs">{p.contact_email}</div></div>
            </div>
            {p.cold_chain_capabilities?.length > 0 && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Cold-chain</div>
                <div className="flex gap-1.5 flex-wrap">
                  {p.cold_chain_capabilities.map((c, i) => <span key={i} className="text-xs mono px-2 py-1 border rounded-full pill-stable">{c}</span>)}
                </div>
              </div>
            )}
            {p.accreditations?.length > 0 && (
              <div className="mt-3 flex gap-1.5 flex-wrap">
                {p.accreditations.map((a, i) => <span key={i} className="text-xs mono px-2 py-1 border rounded-full pill-recovering">{a}</span>)}
              </div>
            )}
          </div>
        ))}
        {providers.length === 0 && <div className="text-stone-500 col-span-2">No providers listed yet.</div>}
      </div>
    </div>
  );
}
